import { type FredScale, formatFredValue } from "@inrev/common";
import { useFetchFredSeries } from "@portal/api/fred/admin";
import { EmptyState } from "@portal/components/patterns";
import { Card, Spinner, Text } from "@portal/components/primitives";
import { useCallback, useMemo } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	type TooltipContentProps,
	XAxis,
	YAxis,
} from "recharts";
import {
	type DatePoint,
	firstPayloadDatum,
	isDatePoint,
	type RechartsTooltipPayload,
} from "./recharts-payload";

const CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: 0 } as const;
const AXIS_TICK = { className: "fill-tertiary text-[11px]" } as const;
const TOOLTIP_CURSOR = { className: "stroke-bc-strong", strokeWidth: 1 } as const;
const TOOLTIP_WRAPPER_STYLE = { outline: "none" } as const;
const GRID = { className: "stroke-bc-default" } as const;
const ACTIVE_DOT = {
	r: 4,
	strokeWidth: 2,
	className: "fill-current stroke-surface-base",
} as const;
/** Rate and index lines use their data range; a zero baseline would flatten their signal. */
const LINE_Y_DOMAIN = ["auto", "auto"] as const;

/** Columns encode magnitude by length, so a truncated baseline would overstate differences. */
const COLUMN_Y_DOMAIN = [0, "auto"] as const;

const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0];

/** Ten years shows an economic cycle without crowding the axis. */
const TREND_YEARS = 10;

/** Below this span, year ticks would repeat, so ticks name the month too. */
const MONTH_TICK_SPAN_YEARS = 3;

/** Preserve full dates so monthly and daily observations do not collapse onto annual ticks. */
type ChartPoint = DatePoint;

const yearLabel = (date: string) => date.slice(0, 4);

const monthLabel = (date: string) =>
	new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});

/** Tick labels for observations running from `first` to `last`, both ISO dates. */
export const trendAxisLabel = (first: string, last: string): ((date: string) => string) =>
	Number(yearLabel(last)) - Number(yearLabel(first)) < MONTH_TICK_SPAN_YEARS
		? monthLabel
		: yearLabel;

/** The ISO date `years` before `isoDate`, or before today when it is undefined. */
const yearsBefore = (isoDate: string | undefined, years: number): string => {
	const date = isoDate === undefined ? new Date() : new Date(`${isoDate}T00:00:00Z`);
	date.setUTCFullYear(date.getUTCFullYear() - years);
	return date.toISOString().slice(0, 10);
};

const tooltipDate = (isoDate: string) =>
	new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});

export type FredTrendTooltipProps = {
	active?: boolean;
	payload?: RechartsTooltipPayload;
	scale: FredScale | undefined;
};

export const FredTrendTooltip = ({ active, payload, scale }: FredTrendTooltipProps) => {
	if (!active || payload === undefined || payload.length === 0) return null;
	const point = firstPayloadDatum(payload, isDatePoint);
	if (point === undefined) return null;

	return (
		<div
			className="flex flex-col gap-2.5 rounded border border-bc-default bg-surface-base px-3 py-2 shadow-md"
			data-testid="fred-trend-tooltip"
		>
			<Text type="caption" color="tertiary">
				{tooltipDate(point.date)}
			</Text>
			<Text type="body-strong" color="primary" className="tabular-nums">
				{scale === undefined ? String(point.value) : formatFredValue(point.value, scale).display}
			</Text>
		</div>
	);
};

export type FredTrendChartProps = {
	seriesCode: string;
	title?: string;
	/** Use columns for discrete magnitudes that require a zero baseline. */
	variant?: "line" | "column";
	/** ISO date. Defaults to `trailingYears` before `to`, or before today. */
	from?: string;
	/** ISO date. Defaults to the latest observation. */
	to?: string;
	/** Length of the window drawn when `from` is not given. */
	trailingYears?: number;
};

export const FredTrendChart = ({
	seriesCode,
	title,
	variant = "line",
	from: fromProp,
	to,
	trailingYears = TREND_YEARS,
}: FredTrendChartProps) => {
	// A window rather than the whole series: daily series run to thousands of points.
	const trailingFrom = useMemo(() => yearsBefore(to, trailingYears), [to, trailingYears]);
	const from = fromProp ?? trailingFrom;
	const query = useFetchFredSeries(seriesCode, from, to);
	const scale = query.data?.series.scale;

	const data = useMemo<ChartPoint[]>(
		() =>
			(query.data?.observations ?? []).map((point) => ({
				date: point.date,
				value: point.value,
			})),
		[query.data?.observations],
	);

	const tickLabel = useMemo(() => {
		const first = data.at(0);
		const last = data.at(-1);
		return first === undefined || last === undefined
			? yearLabel
			: trendAxisLabel(first.date, last.date);
	}, [data]);

	const formatAxis = useCallback(
		(value: number) =>
			scale === undefined ? String(value) : formatFredValue(value, scale).display,
		[scale],
	);

	const tooltip = useCallback(
		({ active, payload }: TooltipContentProps) => (
			<FredTrendTooltip active={active} payload={payload} scale={scale} />
		),
		[scale],
	);

	const heading = title ?? query.data?.series.label ?? seriesCode;

	return (
		<Card elevation="raised" className="shadow-gray-900/10">
			<Card.Header>
				<Card.Title>{heading}</Card.Title>
			</Card.Header>
			<Card.Body className="flex h-[220px] flex-col gap-1 p-4">
				{query.isError ? (
					<div className="flex flex-1 items-center justify-center">
						<Text type="caption" color="danger">
							Could not load {seriesCode}.
						</Text>
					</div>
				) : query.data === undefined ? (
					<div className="flex flex-1 items-center justify-center">
						<Spinner />
					</div>
				) : data.length < 2 ? (
					<EmptyState message="Not enough history to chart." />
				) : (
					<>
						{/* Units named on the chart, never inferred from the numbers. */}
						<Text type="caption" color="tertiary">
							{query.data.series.scaleLabel}, {query.data.series.seasonalAdjustmentLabel}
						</Text>
						<div className="min-h-0 flex-1 text-brand-accent">
							<ResponsiveContainer width="100%" height="100%">
								{variant === "column" ? (
									<BarChart data={data} margin={CHART_MARGIN} barCategoryGap="18%">
										<CartesianGrid {...GRID} vertical={false} strokeDasharray="2 4" />
										<XAxis
											dataKey="date"
											tickFormatter={tickLabel}
											tick={AXIS_TICK}
											tickLine={false}
											axisLine={false}
											interval="preserveStartEnd"
											minTickGap={20}
										/>
										<YAxis
											tick={AXIS_TICK}
											tickLine={false}
											axisLine={false}
											width={56}
											domain={COLUMN_Y_DOMAIN}
											tickFormatter={formatAxis}
										/>
										<Tooltip
											content={tooltip}
											cursor={false}
											wrapperStyle={TOOLTIP_WRAPPER_STYLE}
											isAnimationActive={false}
										/>
										<Bar
											dataKey="value"
											fill="currentColor"
											radius={BAR_RADIUS}
											isAnimationActive={false}
										/>
									</BarChart>
								) : (
									<LineChart data={data} margin={CHART_MARGIN}>
										<CartesianGrid {...GRID} vertical={false} strokeDasharray="2 4" />
										<XAxis
											dataKey="date"
											tickFormatter={tickLabel}
											tick={AXIS_TICK}
											tickLine={false}
											axisLine={false}
											interval="preserveStartEnd"
											minTickGap={40}
										/>
										<YAxis
											tick={AXIS_TICK}
											tickLine={false}
											axisLine={false}
											width={56}
											domain={LINE_Y_DOMAIN}
											tickFormatter={formatAxis}
										/>
										<Tooltip
											content={tooltip}
											cursor={TOOLTIP_CURSOR}
											wrapperStyle={TOOLTIP_WRAPPER_STYLE}
											isAnimationActive={false}
										/>
										<Line
											type="linear"
											dataKey="value"
											stroke="currentColor"
											strokeWidth={2}
											strokeLinecap="round"
											dot={false}
											activeDot={ACTIVE_DOT}
											isAnimationActive={false}
										/>
									</LineChart>
								)}
							</ResponsiveContainer>
						</div>
					</>
				)}
			</Card.Body>
		</Card>
	);
};
